import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { Check, Play, Square, X } from "lucide-react";
import { loadCurrent, addToHistory } from "@/lib/instant";
import { getDeviceId } from "@/lib/profile";
import AudioPlayer from "@/components/julie/AudioPlayer";
import VideoPlayer from "@/components/julie/VideoPlayer";
import Lightbox from "@/components/julie/Lightbox";
import { cutOpeningSound } from "@/lib/openingSound";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];
const FEELINGS = ["Beaucoup mieux", "Un peu mieux", "Plus calme", "Plus énergique", "Plus connectée à moi", "À peu près pareil"];
const VIDEO_EXTS = ["mp4", "webm", "mov"];
const IMAGE_EXTS = ["png", "jpg", "jpeg", "webp"];
const extOf = (p) => (p || "").split("?")[0].split(".").pop().toLowerCase();

export default function Session() {
  const navigate = useNavigate();
  const instant = useMemo(loadCurrent, []);
  const [idx, setIdx] = useState(0);
  const [left, setLeft] = useState(null);
  const [started, setStarted] = useState(false);
  const [paused, setPaused] = useState(false);
  const [phase, setPhase] = useState("run");
  const [instantId, setInstantId] = useState(null);
  const [creditsRemaining, setCreditsRemaining] = useState(null);
  const [resalibUrl, setResalibUrl] = useState(""); // agenda de Julie (réglage admin Resalib)
  const [saved, setSaved] = useState(false);
  const [stoppedAt, setStoppedAt] = useState(null);
  // Média de l'étape : la voix guidée / la vidéo / l'image du contenu (media_path)
  // prime ; sinon le bruitage choisi par Julie tourne en boucle ; sinon le fond par
  // défaut. Les supports s'enchaînent d'étape en étape, quel que soit leur format.
  const stepSound = instant?.steps?.[idx]?.sound || null;
  const stepMedia = instant?.steps?.[idx]?.media_path || null;
  const mediaUrl = stepMedia ? `${process.env.REACT_APP_BACKEND_URL}/api/media/${stepMedia}` : null;
  // Type du support selon l'extension du fichier
  const mediaKind = !stepMedia ? "audio" : VIDEO_EXTS.includes(extOf(stepMedia)) ? "video" : IMAGE_EXTS.includes(extOf(stepMedia)) ? "image" : "audio";
  // Source du LECTEUR AUDIO : la voix guidée (media audio) est jouée UNE fois ;
  // quand elle se termine, l'étape s'achève et la séance enchaîne (demande de
  // Julie : un contenu fini ne redémarre JAMAIS tout seul). Pour une vidéo, le
  // lecteur natif suffit. UNE IMAGE SE REGARDE EN SILENCE — aucun son ajouté.
  const guidedSrc = mediaKind === "audio" && stepMedia ? mediaUrl : null;
  const soundSrc = guidedSrc; // uniquement la voix guidée ; rien pour image ni vidéo
  const soundLoop = false; // jamais de boucle : un contenu terminé ne redémarre pas
  const [zoomSrc, setZoomSrc] = useState(null); // image agrandie en plein écran (la séance continue)

  const togglePause = () => setPaused((p) => !p);

  // La mer de la première page s'arrête NET dès l'arrivée sur la séance
  useEffect(() => {
    cutOpeningSound();
    // Agenda de Julie pour le bouton « J'ai besoin de plus » (fin de séance)
    axios.get(`${API}/settings/public`).then(({ data }) => setResalibUrl(data.settings?.resalib_url || "")).catch(() => {});
  }, []);

  // Séance en cours : les bandeaux haut/bas restent visibles mais deviennent inactifs (sauf la langue)
  useEffect(() => {
    const live = started && phase !== "done";
    document.body.classList.toggle("rav-session-live", live);
    return () => document.body.classList.remove("rav-session-live");
  }, [started, phase]);

  const postedRef = useRef(false);

  // Démarrer la séance : C'EST ICI — et seulement ici — que l'Instant est débité (jamais à la simple ouverture).
  // Exception : une REPRISE de séance stoppée ne débite pas à nouveau — c'est la même séance.
  const startSession = () => {
    cutOpeningSound(); // la vague s'arrête net au moment où la séance démarre
    if (instant.resumePct != null) {
      setStarted(true);
      setPaused(false);
      return;
    }
    if (postedRef.current) return;
    postedRef.current = true;
    const token = localStorage.getItem("rav-token");
    axios
      .post(
        `${API}/instants`,
        { pillar: instant.pillar, minutes: instant.minutes, steps: instant.steps, device_id: getDeviceId() },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {},
      )
      .then(({ data }) => {
        setInstantId(data.id);
        setCreditsRemaining(data.credits_remaining ?? null);
        setStarted(true);
        setPaused(false);
      })
      .catch((err) => {
        postedRef.current = false;
        if (err.response?.status === 402) {
          toast(err.response.data.detail);
          navigate("/offres");
        } else {
          toast("La séance n'a pas pu démarrer — réessayez.");
        }
      });
  };

  useEffect(() => {
    if (!instant) {
      navigate("/");
      return;
    }
    // Pas de débit ici : l'Instant n'est compté qu'au clic sur « Démarrer ma séance »
    if (instant.resumePct != null) {
      // Reprise d'une séance stoppée : on se replace à l'étape et au temps où elle en était
      const totalAll = instant.steps.reduce((s, x) => s + x.minutes * 60, 0);
      let elapsed = (instant.resumePct / 100) * totalAll;
      let i = 0;
      while (i < instant.steps.length - 1 && elapsed >= instant.steps[i].minutes * 60) {
        elapsed -= instant.steps[i].minutes * 60;
        i += 1;
      }
      setIdx(i);
      setLeft(Math.max(1, instant.steps[i].minutes * 60 - Math.floor(elapsed)));
    } else {
      setLeft(instant.steps[0].minutes * 60);
    }
  }, []);

  useEffect(() => {
    if (phase !== "run" || !started || paused || left === null || !instant) return undefined;
    if (left <= 0) {
      if (idx < instant.steps.length - 1) setPhase("transition");
      else setPhase("done");
      return undefined;
    }
    const t = setInterval(() => setLeft((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [phase, paused, left, idx, instant, started]);

  // Transition vers l'étape suivante : effet DÉDIÉ (le mettre dans l'effet du
  // minuteur faisait annuler le setTimeout quand la phase changeait → séance
  // bloquée pour toujours sur « Prenez quelques secondes… »)
  useEffect(() => {
    if (phase !== "transition" || !instant) return undefined;
    const t = setTimeout(() => {
      setIdx((i) => {
        const next = i + 1;
        setLeft(instant.steps[next].minutes * 60);
        return next;
      });
      setPhase("run");
    }, 2500);
    return () => clearTimeout(t);
  }, [phase, instant]);

  if (!instant) return null;

  const step = instant.steps[idx];
  const total = step.minutes * 60;
  const pct = Math.min(100, ((total - (left ?? 0)) / total) * 100);

  // Progression globale de la séance (toutes les étapes confondues), pour l'historique
  const totalAll = instant.steps.reduce((s, x) => s + x.minutes * 60, 0);
  const elapsedPrev = instant.steps.slice(0, idx).reduce((s, x) => s + x.minutes * 60, 0);
  const sessionPct = Math.max(1, Math.min(100, Math.round(((elapsedPrev + (total - (left ?? total))) / totalAll) * 100)));
  // Pastille : le TEMPS QU'IL RESTE à écouter pour toute la séance (suit en direct,
  // dans les deux sens — si l'on revient en arrière, il remonte aussi)
  const remainingAll = Math.max(0, (left ?? total) + instant.steps.slice(idx + 1).reduce((s, x) => s + x.minutes * 60, 0));
  const remainingLabel = remainingAll >= 60 ? `${Math.ceil(remainingAll / 60)} min` : `${Math.round(remainingAll)} s`;

  const finishStep = () => setLeft(0);

  // Navigation libre entre les contenus de l'Instant (audio, image ou vidéo)
  const goToStep = (i) => {
    if (i < 0) return;
    if (i >= instant.steps.length) {
      setPhase("done"); // « Contenu suivant » sur le dernier = fin de l'Instant
      return;
    }
    setIdx(i);
    setLeft(instant.steps[i].minutes * 60);
    setPhase("run");
    setPaused(false);
  };

  // « Arrêter la séance » : la séance s'interrompt et mène à l'écran de ressenti —
  // elle n'est PAS finie : on peut la reprendre là où elle en était, ou donner son ressenti pour terminer
  const stopSession = () => {
    setStoppedAt(sessionPct);
    setPhase("done");
  };

  const resumeSession = () => {
    setStoppedAt(null);
    setPaused(false);
    setPhase("run");
  };

  const chooseFeeling = (feeling) => {
    if (saved) return;
    setSaved(true);
    addToHistory({
      id: Date.now(),
      date: new Date().toISOString(),
      pillar: instant.pillar,
      minutes: instant.minutes,
      steps: instant.steps,
      feelingAfter: feeling,
      note: "",
      favorite: false,
      ...(stoppedAt != null ? { stoppedPct: stoppedAt } : {}),
    });
    if (instantId) {
      axios.post(`${API}/instants/${instantId}/complete`, { feeling_after: feeling }).catch(() => {});
      if (stoppedAt != null) {
        axios.post(`${API}/instants/${instantId}/stop`, { progress_pct: stoppedAt }).catch(() => {});
      }
    }
  };

  return (
    <main className="rav-page rav-session" data-testid="session-page">
      <button type="button" className="rav-overlay-close" data-testid="session-close" aria-label="Quitter" onClick={() => navigate("/")}>
        <X size={19} strokeWidth={1.5} />
      </button>

      <AnimatePresence mode="wait">
        {phase === "run" && (
          <motion.div key={`run-${idx}`} className="rav-session-stage" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }}>
            <p className="rav-session-kicker" data-testid="session-step-label">
              {step.label} — {step.minutes} min
            </p>
            <motion.div className="rav-breath" data-testid="session-breath-circle">
              <motion.div
                className="rav-breath-inner"
                animate={paused || !started ? { scale: 0.5 } : { scale: [0.3, 0.68, 0.68, 0.3] }}
                transition={{ duration: 10, repeat: Infinity, times: [0, 0.4, 0.62, 1], ease: "easeInOut" }}
              />
            </motion.div>
            <h1 className="rav-page-title" data-testid="session-step-title">« {step.title} »</h1>
            <p className="rav-page-sub rav-session-desc">{step.description}</p>
            <div
              className="rav-progress"
              data-testid="session-progress"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={total}
              aria-valuenow={total - (left ?? 0)}
            >
              <div className="rav-progress-fill" style={{ width: `${pct}%` }} />
              <div className="rav-progress-knob" data-testid="session-progress-knob" style={{ left: `${pct}%` }}>
                {remainingLabel}
              </div>
            </div>
            {started && mediaKind === "video" && mediaUrl && (
              <VideoPlayer
                src={mediaUrl}
                playing={started && phase === "run" && !paused}
                onToggle={togglePause}
                onEnded={() => setLeft(0)} // vidéo finie → l'étape s'achève, la séance enchaîne
                testId="session-video"
              />
            )}
            {started && mediaKind === "image" && mediaUrl && (
              <>
                {/* Un appui agrandit l'image en plein écran — la séance continue, rien ne se réinitialise */}
                <img
                  className="rav-session-image rav-session-image-zoomable"
                  data-testid="session-image"
                  src={mediaUrl}
                  alt={step.title}
                  onClick={() => setZoomSrc(mediaUrl)}
                  role="button"
                  aria-label="Agrandir l'image"
                  title="Toucher pour agrandir"
                />
                <Lightbox src={zoomSrc} alt={step.title} onClose={() => setZoomSrc(null)} testId="session-lightbox" />
              </>
            )}
            {started && mediaKind !== "video" && soundSrc && (
              <AudioPlayer
                src={soundSrc}
                playing={started && phase === "run" && !paused}
                onToggle={togglePause}
                onEnded={() => setLeft(0)}  // contenu fini → l'étape s'achève, la séance enchaîne
                loop={soundLoop}
                testId="session-player"
              />
            )}
            <div className="rav-session-actions">
              {!started && (
                <button
                  type="button"
                  className="rav-primary-button rav-session-start"
                  data-testid="session-start-button"
                  onClick={startSession}
                >
                  <Play size={16} strokeWidth={1.6} />
                  Démarrer ma séance
                </button>
              )}
              {started && phase === "run" && (
                <div className="rav-session-actions" data-testid="session-step-nav" style={{ gap: 10 }}>
                  {instant.steps.length > 1 && (
                    <button
                      type="button"
                      className="rav-secondary-button rav-session-btn"
                      data-testid="session-prev-step"
                      disabled={idx === 0}
                      onClick={() => goToStep(idx - 1)}
                      style={{ opacity: idx === 0 ? 0.4 : 1 }}
                    >
                      ← Contenu précédent
                    </button>
                  )}
                  <button
                    type="button"
                    className="rav-secondary-button rav-session-btn"
                    data-testid="session-goto-next"
                    onClick={() => goToStep(idx + 1)}
                  >
                    {idx < instant.steps.length - 1 ? "Contenu suivant →" : "Terminer mon Instant →"}
                  </button>
                </div>
              )}
              {started && (
                <button
                  type="button"
                  className="rav-secondary-button rav-session-btn rav-session-stop"
                  data-testid="session-stop-button"
                  onClick={stopSession}
                >
                  <Square size={14} strokeWidth={1.6} />
                  Arrêter la séance
                </button>
              )}
            </div>
          </motion.div>
        )}

        {phase === "transition" && (
          <motion.div key="transition" className="rav-session-stage" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }}>
            <span className="rav-session-check"><Check size={26} strokeWidth={1.6} /></span>
            <p className="rav-page-title" data-testid="session-transition">{step.label} terminé</p>
            <p className="rav-page-sub">Prenez quelques secondes…</p>
            {/* Porte de secours : si l'enchaînement automatique tardait, on passe à la suite d'un toucher */}
            <button
              type="button"
              className="rav-text-link"
              data-testid="session-next-step"
              onClick={() => {
                setIdx((i) => {
                  const next = i + 1;
                  setLeft(instant.steps[next].minutes * 60);
                  return next;
                });
                setPhase("run");
              }}
            >
              Passer à la suite
            </button>
          </motion.div>
        )}

        {phase === "done" && !saved && (
          <motion.div key="done" className="rav-session-stage" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE }}>
            {/* PAGE 1 de fin : le ressenti après séance, rien d'autre */}
            <p className="rav-page-title" data-testid="session-done-title">Comment vous sentez-vous maintenant ?</p>
            {stoppedAt != null && (
              <>
                <p className="rav-page-sub" data-testid="session-stopped-note">
                  Séance arrêtée à {stoppedAt} % — elle n'est pas finie, reprenez-la quand vous voulez.
                </p>
                <div className="rav-session-actions">
                  <button
                    type="button"
                    className="rav-primary-button rav-session-start"
                    data-testid="session-resume-button"
                    onClick={resumeSession}
                  >
                    <Play size={16} strokeWidth={1.6} />
                    Reprendre ma séance
                  </button>
                </div>
                <p className="rav-page-sub">Ou dites-nous votre ressenti pour la terminer :</p>
              </>
            )}
            <div className="rav-chips" data-testid="feeling-options">
              {FEELINGS.map((f) => (
                <button key={f} type="button" className="rav-chip" data-testid={`feeling-${f}`} onClick={() => chooseFeeling(f)}>
                  {f}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {phase === "done" && saved && (
          <motion.div key="finished" className="rav-session-stage" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE }}>
            {/* PAGE 2 de fin : terminé + deux boutons, rien d'autre */}
            <p className="rav-page-title" data-testid="session-finished-title">Votre Instant est terminé 🤎</p>
            <div className="rav-session-actions">
              <button type="button" className="rav-primary-button rav-session-btn" data-testid="session-home-button" onClick={() => navigate("/")}>
                Retour à l'accueil
              </button>
              <button
                type="button"
                className="rav-secondary-button rav-session-btn"
                data-testid="session-another"
                onClick={() => {
                  sessionStorage.removeItem("rav-current-instant");
                  navigate("/accueil"); // parcours de création : choix du ressenti
                }}
              >
                Créer un nouvel Instant
              </button>
              <button
                type="button"
                className="rav-secondary-button rav-session-btn"
                data-testid="session-need-more"
                onClick={() => {
                  // « J'ai besoin de plus » : rendez-vous avec Julie — directement sa page
                  // Resalib si elle est renseignée, sinon la page Rendez-vous de l'app
                  if (resalibUrl) window.open(resalibUrl, "_blank", "noopener,noreferrer");
                  else navigate("/reservation");
                }}
              >
                J'ai besoin de plus — prendre rendez-vous avec Julie
              </button>
            </div>
            {creditsRemaining === 0 && (
              <motion.div
                className="rav-limit-card"
                data-testid="session-credits-empty"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
              >
                <p className="rav-limit-text">
                  Vous venez de savourer votre dernier Instant offert. Respire Aime Vibre + vous ouvre des Instants illimités.
                </p>
                <button
                  type="button"
                  className="rav-primary-button"
                  data-testid="session-offers-button"
                  onClick={() => navigate("/offres")}
                >
                  Découvrir Respire Aime Vibre +
                </button>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
